/**
 * DishScanner Component — Camera capture and AI dish recognition (refined)
 */
import React, { useState, useCallback } from 'react';
import { Camera, Search, AlertCircle, CheckCircle } from 'lucide-react';
import { Modal, Spinner, ErrorMessage } from '../../shared';
import { useCamera } from '../../../hooks/useCamera';
import { dishApi } from '../../../services/api';

export function DishScanner({ isOpen, onClose, onRestaurantSelect, t }) {
  const { videoRef, isActive, error: cameraError, startCamera, stopCamera, capturePhoto } = useCamera();
  const [description, setDescription] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleOpen = useCallback(async () => {
    setResult(null);
    setDescription('');
    setError(null);
  }, []);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);

    try {
      let imageData = null;
      if (isActive) {
        imageData = capturePhoto();
      }

      const response = await dishApi.analyze({
        image: imageData,
        description: description || undefined
      });

      setResult(response);
    } catch (err) {
      setError(err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleViewRestaurant = () => {
    if (result?.restaurant && onRestaurantSelect) {
      onRestaurantSelect(result.restaurant);
      onClose();
    }
  };

  const handleClose = () => {
    stopCamera();
    setResult(null);
    setDescription('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="space-y-4">
        {!result && (
          <>
            <div className="relative bg-stone-900 rounded-2xl overflow-hidden aspect-video">
              {isActive ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
                  <button
                    onClick={startCamera}
                    className="flex flex-col items-center gap-2 text-white hover:text-primary-400 transition-colors duration-200"
                  >
                    <Camera size={44} />
                    <span className="text-sm font-medium">Tap to start camera</span>
                  </button>
                </div>
              )}

              {isActive && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-44 h-44 border-2 border-primary-400/60 rounded-2xl animate-pulse-soft" />
                </div>
              )}

              {cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-900/80">
                  <div className="text-center text-white p-4">
                    <AlertCircle className="mx-auto mb-2 text-red-400" size={28} />
                    <p className="text-sm font-medium">{cameraError}</p>
                    <p className="text-xs text-stone-400 mt-1">Try describing the dish instead</p>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder="Or describe the dish (e.g., 'spicy noodle soup with shrimp')"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-stone-100 rounded-xl px-4 py-3 pr-12 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:bg-white transition-all duration-200"
              />
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
            </div>

            <button
              onClick={handleAnalyze}
              disabled={analyzing || (!isActive && !description)}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.97]"
            >
              {analyzing ? (
                <>
                  <Spinner size="sm" className="text-white" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Camera size={16} />
                  Analyze Dish
                </>
              )}
            </button>
          </>
        )}

        {result && (
          <div className="text-center animate-slideUp">
            <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="text-emerald-500" size={28} />
            </div>

            <p className="font-bold text-lg text-stone-800">{result.dishName}</p>
            {result.thaiName && (
              <p className="text-sm text-stone-400 font-medium">{result.thaiName}</p>
            )}

            {result.description && (
              <p className="text-sm text-stone-500 mt-2 leading-relaxed">{result.description}</p>
            )}

            {result.restaurant && (
              <div className="mt-4 p-3 bg-warm-50 rounded-xl border border-warm-200">
                <p className="text-sm text-stone-600 mb-2">
                  Found at <span className="font-semibold text-primary-600">{result.restaurant.name}</span>
                </p>
                <button
                  onClick={handleViewRestaurant}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-semibold transition-all duration-200"
                >
                  View Restaurant
                </button>
              </div>
            )}

            {result.confidence && (
              <p className="text-[11px] text-stone-400 mt-3 font-medium">
                Confidence: {Math.round(result.confidence * 100)}%
              </p>
            )}

            {result.similarDishes?.length > 0 && (
              <div className="mt-4 text-left">
                <p className="text-[11px] font-semibold text-stone-500 mb-2 uppercase tracking-wider">Similar dishes</p>
                <div className="space-y-1">
                  {result.similarDishes.map((dish, i) => (
                    <p key={i} className="text-xs text-stone-500">
                      • {dish.name} {dish.restaurantName && `@ ${dish.restaurantName}`}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => { setResult(null); stopCamera(); }}
              className="mt-4 text-sm text-stone-400 hover:text-stone-600 font-medium transition-colors"
            >
              Scan another dish
            </button>
          </div>
        )}

        {error && <ErrorMessage error={error} onRetry={handleAnalyze} />}
      </div>
    </Modal>
  );
}

export default DishScanner;
